import { v4 as uuid } from "uuid";
import moment from "moment";
import Dexie from "dexie";
import { appStore } from "../lfStorage/lfStorage";
import download from "downloadjs";
import { exportDB, peakImportFile, importDB } from "dexie-export-import";
import { v10 } from "./versions.js";

export class LocalDbError extends Error {
  constructor(message, code) {
    super();
    this.message = message;
    this.code = code;
  }
}
export class LocalDbAuthenticationError extends LocalDbError {
  constructor(message, code) {
    super();
    this.message = message;
    this.code = code;
  }
}

function handleLocalDbError(error) {
  console.error(error);
  if (error instanceof LocalDbAuthenticationError) {
    throw error;
  }
  throw new LocalDbError(`Local DB error ${error.message}`);
}

/**
 * Intenta recuperar el userName desde las bases de datos Dexie existentes
 * @returns {Promise<string|null>} El userName si se encuentra una base de datos válida, null en caso contrario
 */
async function recoverUserNameFromDexie() {
  try {
    // Obtener todas las bases de datos Dexie existentes
    const dbNames = await Dexie.getDatabaseNames();

    if (!dbNames || dbNames.length === 0) {
      console.log("No se encontraron bases de datos Dexie");
      return null;
    }

    // Intentar encontrar una base de datos que contenga datos válidos
    for (const dbName of dbNames) {
      try {
        // Crear instancia temporal de la base de datos
        const tempDb = new Dexie(dbName);
        tempDb.version(v10.versionNumber)
          .stores(v10.stores)
          .upgrade(async (trx) => {
            await Promise.all(v10.migrations.map((migration) => migration({ transaction: trx })));
          });

        // Verificar si la base de datos tiene datos válidos
        // Intentamos acceder a la tabla de eventos o farms para validar
        await tempDb.open();

        // Verificar si tiene eventos o farms (datos válidos)
        const eventsCount = await tempDb.events.count();
        const farmsCount = await tempDb.farms.count();

        // Si tiene al menos eventos o farms, consideramos que es una base de datos válida
        if (eventsCount > 0 || farmsCount > 0) {
          console.log(`Base de datos válida encontrada: ${dbName} (${eventsCount} eventos, ${farmsCount} farms)`);
          await tempDb.close();
          return dbName;
        }

        await tempDb.close();
      } catch (e) {
        // Si hay error al abrir o acceder a la base de datos, continuar con la siguiente
        console.warn(`Error al verificar base de datos ${dbName}:`, e.message);
        continue;
      }
    }

    console.log("No se encontró ninguna base de datos Dexie con datos válidos");
    return null;
  } catch (e) {
    console.error("Error al recuperar userName desde Dexie:", e);
    return null;
  }
}

async function openDb() {
  let currentUser = await appStore.get("currentUser");

  // Si no hay currentUser, intentar recuperar el userName desde las bases de datos existentes
  if (!currentUser) {
    console.warn("currentUser no encontrado en appStore, intentando recuperar desde Dexie...");
    const recoveredUserName = await recoverUserNameFromDexie();

    if (recoveredUserName) {
      // Crear un objeto currentUser mínimo con el userName recuperado
      currentUser = { userName: recoveredUserName };
      console.log(`Usuario recuperado desde Dexie: ${recoveredUserName}`);

      // Intentar guardar el currentUser recuperado en appStore
      try {
        await appStore.set("currentUser", currentUser);
      } catch (e) {
        console.warn("No se pudo guardar el currentUser recuperado en appStore:", e);
        // Continuamos de todas formas con el currentUser recuperado
      }
    } else {
      // Si no se puede recuperar, lanzar error de autenticación
      handleLocalDbError(new LocalDbAuthenticationError(`Login needed`, 401));
    }
  }

  // Validar que currentUser tiene userName
  if (!currentUser || !currentUser.userName) {
    handleLocalDbError(new LocalDbAuthenticationError(`Login needed - userName no disponible`, 401));
  }

  let db = new Dexie(currentUser.userName);
  db.version(v10.versionNumber)
    .stores(v10.stores)
    .upgrade(async (trx) => {
      await Promise.all(v10.migrations.map((migration) => migration({ transaction: trx })));
    });
  return db;
}

export async function logChange(entity, entityId, updatedFields, type, db) {
  try {
    await db.changelog.put({
      id: uuid(),
      monitoringEventId: localStorage.getItem("currentEventId"),
      entity: entity,
      entityId: entityId,
      type: type,
      changedFields: updatedFields,
      syncStatus: "not-synced",
      lastSyncDate: null,
      timestamp: moment().format("x"),
    });
  } catch (e) {
    handleLocalDbError(e);
  }
}

export async function getPendingChanges(monitoringEventId) {
  try {
    let db = await openDb();
    return await db.changelog
      .where("syncStatus")
      .anyOf(["not-synced", "failed"])
      .and((item) => item.monitoringEventId === monitoringEventId)
      .sortBy("timestamp");
  } catch (e) {
    handleLocalDbError(e);
  }
}

export async function removeChanges(entity, entityId) {
  try {
    let db = await openDb();
    await db.changelog.where("entity").equals(entity).and("entityId").equals(entityId).delete();
  } catch (e) {
    handleLocalDbError(e);
  }
}

export async function remove(table, id, log = true, dbObject) {
  try {
    const db = dbObject || (await openDb());
    await db[table].delete(id);
    await removeChanges(table, id);
    if (log) await logChange(table, id, [], "delete", db);
  } catch (e) {
    handleLocalDbError(e);
  }
}

export async function update(table, id, data, log = true, dbObject) {
  try {
    const db = dbObject || (await openDb());
    await db[table].update(id, { ...data, _rev: uuid() });
    if (log) await logChange(table, id, Object.keys(data), "update", db);
  } catch (e) {
    handleLocalDbError(e);
  }
}

export async function put(table, id, data, log = true, dbObject) {
  try {
    const db = dbObject || (await openDb());
    await db[table].put(data, id);
    if (log) await logChange(table, id, Object.keys(data), "put", db);
  } catch (e) {
    handleLocalDbError(e);
  }
}

export async function upsert(table, id, data, log = true, dbObject) {
  try {
    const db = dbObject || (await openDb());
    const exists = await db[table].get(id);
    if (exists) {
      await db[table].update(data, id);
      if (log) await logChange(table, id, Object.keys(data), "update", db);
    } else {
      await db[table].put(data, id);
      if (log) await logChange(table, id, Object.keys(data), "put", db);
    }
  } catch (e) {
    handleLocalDbError(e);
  }
}

export async function bulkPut(table, data, log = true, dbObject) {
  try {
    const db = dbObject || (await openDb());
    await db.transaction("rw", [table, "changelog"], async () => {
      await db[table].bulkPut(data);
      if (log) {
        await Promise.all(
          data.map((record) => {
            return logChange(table, record.id, Object.keys(record), "put", db);
          })
        );
      }
    });
  } catch (e) {
    handleLocalDbError(e);
  }
}

export async function getOne(table, id, dbObject) {
  try {
    const db = dbObject || (await openDb());
    let data = await db[table].get(id);
    return data;
  } catch (e) {
    handleLocalDbError(e);
  }
}

export async function getMany(table, dbObject) {
  try {
    const db = dbObject || (await openDb());
    return await db[table];
  } catch (e) {
    handleLocalDbError(e);
  }
}

export async function exportDb({ downloadFile, progressCallback }) {
  try {
    let db = await openDb();
    const blob = await exportDB(db, { prettyJson: true, progressCallback });
    if (downloadFile) {
      return download(blob, "dexie-export.json", "application/json");
    } else {
      return blob;
    }
  } catch (e) {
    handleLocalDbError(e);
  }
}

export async function importDb({ fileData, options }) {
  try {
    return await importDB(fileData, {
      ...options,
      clearTablesBeforeImport: true,
      overwriteValues: true,
      acceptVersionDiff: true,
    });
  } catch (e) {
    handleLocalDbError(e);
  }
}

export async function peakInfo({ fileData }) {
  try {
    return await peakImportFile(fileData);
  } catch (e) {
    handleLocalDbError(e);
  }
}

export async function getDb() {
  return await openDb();
}
